"use client";

import { formatAccountNumber, parseAccountNumber } from "@/lib/account-number";
import { OneViewMark } from "@/components/desktop/api-source";
import { Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Chip, Kv, Panel, PanelHead, Pill, StatusTone } from "@/components/desktop/chrome";
import type { Permissions } from "@/lib/auth/roles";
import { minRoleFor } from "@/lib/auth/roles";
import type { Customer360 } from "@/lib/types";

type Tab = "overview" | "billing" | "services" | "interactions" | "tickets";

function displayAccountNumber(value: string): string {
  try {
    return formatAccountNumber(parseAccountNumber(value).legacyAccountNumber);
  } catch {
    return value;
  }
}

export function CustomerBand({
  customer,
  verified,
  onVerify,
  onTicket,
}: {
  customer: Customer360;
  verified: boolean;
  onVerify: () => void;
  onTicket: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-[10px] border border-[#dc2626] bg-card px-4 py-3.5 shadow-[0_1px_2px_rgba(22,35,46,.08),0_4px_16px_rgba(22,35,46,.06)] ring-1 ring-[#dc2626]/50">
      <div>
        <h1 className="inline-flex items-center gap-1.5 text-[19px] font-bold">
          {customer.name}
          <OneViewMark source="customerName" compact />
        </h1>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-[12.5px] text-[#43535f]">
          Account{" "}
          <span className="inline-flex items-center gap-1">
            <span className="num">{displayAccountNumber(customer.accountNumber)}</span>
            <OneViewMark source="accountNumber" compact />
          </span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            {customer.productsLabel}
            <OneViewMark source="products" compact />
          </span>
          <span>· Customer since</span>
          <span className="inline-flex items-center gap-1">
            {customer.customerSince}
            <OneViewMark source="tenure" compact />
          </span>
          <span>· Preferred language:</span>
          <span className="inline-flex items-center gap-1">
            {customer.preferredLanguage}
            <OneViewMark source="language" compact />
          </span>
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Chip tone="ok">
          {customer.standing}
          <OneViewMark source="standing" compact />
        </Chip>
        <Chip>
          {customer.autoPay ? "AutoPay on" : "AutoPay off"}
          <OneViewMark source="autoPay" compact />
        </Chip>
        <Chip tone={verified ? "ok" : "warn"}>{customer.verifyChip}</Chip>
      </div>
      <div className="ml-auto flex gap-2">
        <Button variant="outline" onClick={onTicket}>Create ticket</Button>
        {!verified && <Button onClick={onVerify}>Verify identity</Button>}
      </div>
    </div>
  );
}

export function VerifyBanner({ verified, method, onVerify }: { verified: boolean; method?: string; onVerify: () => void }) {
  if (verified) {
    return (
      <div className="mt-3 flex items-center gap-3 rounded-md border border-[#bfe0cd] bg-ok-wash px-3.5 py-2.5 text-[13px] text-ok" role="status">
        <div className="flex-1">
          <strong>Identity verified</strong> — {method} · valid for this interaction only. Protected details are now visible.
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-3 rounded-md border border-[#ecd9b4] bg-warn-wash px-3.5 py-2.5 text-[13px] text-warn" role="status">
      <Lock className="size-4 shrink-0" />
      <div className="flex-1">
        <strong>Verify the customer before sharing account details.</strong> Call records, usage, and full contact details stay hidden until identity is confirmed (CPNI).
      </div>
      <Button size="sm" variant="outline" onClick={onVerify}>Verify now</Button>
    </div>
  );
}

export function CustomerTabs({ tab, ticketCount, onTab }: { tab: Tab; ticketCount: number; onTab: (tab: Tab) => void }) {
  const items: { id: Tab; label: string; count?: number }[] = [
    { id: "overview", label: "Overview" },
    { id: "billing", label: "Billing & payments" },
    { id: "services", label: "Services & devices" },
    { id: "interactions", label: "Interactions" },
    { id: "tickets", label: "Tickets", count: ticketCount },
  ];
  return (
    <div className="mb-3.5 flex gap-0.5 border-b border-border" role="tablist">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={tab === item.id}
          onClick={() => onTab(item.id)}
          className={`-mb-px px-3.5 py-2 text-[13px] font-semibold ${tab === item.id ? "border-b-2 border-primary text-primary" : "text-[#43535f] hover:text-foreground"}`}
        >
          {item.label}
          {item.count != null && (
            <span className="ml-1.5 rounded-full border border-[#e6ebf0] bg-secondary px-1.5 text-[11px] text-[#43535f] num">{item.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function OverviewTab({ customer, onConsent }: { customer: Customer360; onConsent: () => void }) {
  const s = customer.stats;
  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: "Current balance", value: s.currentBalance, meta: s.dueMeta, source: "balance" as const },
          { label: "Last invoice", value: s.lastInvoice, meta: s.lastInvoiceMeta, source: "lastInvoice" as const },
          { label: "Active services", value: s.activeServices, meta: s.activeServicesMeta, source: "activeServices" as const },
          { label: "Open tickets", value: s.openTickets, meta: s.openTicketsMeta, source: null },
        ].map((stat) => (
          <div
            key={stat.label}
            className={`rounded-[10px] border bg-card px-3.5 py-3 ${stat.source ? "border-[#dc2626] ring-1 ring-[#dc2626]/50" : "border-border"}`}
          >
            <div className="flex items-center gap-1.5 text-[11.5px] font-semibold text-muted-foreground">
              {stat.label}
              {stat.source && <OneViewMark source={stat.source} compact />}
            </div>
            <div className="mt-0.5 font-[family-name:var(--font-heading)] text-[21px] font-bold num">{stat.value}</div>
            <div className="mt-0.5 text-[11.5px] text-[#43535f]">{stat.meta}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-3.5 md:grid-cols-2">
        <Panel oneview>
          <PanelHead title="Contact details">
            <OneViewMark source="contactMedia" />
            <span className="flex-1" />
            {customer.contact.masked && (
              <span className="inline-flex items-center gap-1 rounded-full bg-warn-wash px-2 py-0.5 text-[11.5px] font-semibold text-warn">
                Verify to view
              </span>
            )}
          </PanelHead>
          <div className="px-4 py-3.5">
            <Kv
              items={[
                { label: "Mobile", mark: <OneViewMark source="contactMedia" compact />, value: <span className={customer.contact.masked ? "tracking-wider text-muted-foreground" : "num"}>{customer.contact.mobile}</span> },
                { label: "Email", mark: <OneViewMark source="contactMedia" compact />, value: <span className={customer.contact.masked ? "tracking-wider text-muted-foreground" : ""}>{customer.contact.email}</span> },
                { label: "Service address", mark: <OneViewMark source="serviceAddress" compact />, value: <span className={customer.contact.masked ? "tracking-wider text-muted-foreground" : ""}>{customer.contact.serviceAddress}</span> },
                { label: "Billing address", mark: <OneViewMark source="billingAddress" compact />, value: customer.contact.billingAddress },
                { label: "Contact preference", mark: <OneViewMark source="contactPreference" compact />, value: customer.contact.contactPreference },
              ]}
            />
          </div>
        </Panel>
        <Panel oneview>
          <PanelHead title="Account health">
            <OneViewMark source="paymentHistory" />
          </PanelHead>
          <div className="px-4 py-3.5">
            <Kv
              items={[
                { label: "Payment history", mark: <OneViewMark source="paymentHistory" compact />, value: customer.health.paymentHistory },
                { label: "Tenure", mark: <OneViewMark source="tenure" compact />, value: customer.health.tenure },
                { label: "Contract", mark: <OneViewMark source="contract" compact />, value: customer.health.contract },
                { label: "App engagement", value: customer.health.appEngagement },
                {
                  label: "Churn signal",
                  value: (
                    <span>
                      <Pill tone="warn">{customer.health.churnLabel}</Pill>{" "}
                      <span className="text-[11.5px] text-muted-foreground">{customer.health.churnNote}</span>
                    </span>
                  ),
                },
              ]}
            />
          </div>
        </Panel>
      </div>
      <div className="grid gap-3.5 md:grid-cols-2">
        <Panel oneview>
          <PanelHead title="Authorized users">
            <OneViewMark source="authorizedUsers" />
            <span className="flex-1" />
            <span className="text-[11.5px] text-muted-foreground">Who may act on this account</span>
          </PanelHead>
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
                <th className="px-3 py-2">Person</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">May do</th>
              </tr>
            </thead>
            <tbody>
              {customer.authorizedUsers.map((row) => (
                <tr key={row.person} className="border-t border-[#e6ebf0]">
                  <td className="px-3 py-2">{row.person}</td>
                  <td className="px-3 py-2">{row.role}</td>
                  <td className="px-3 py-2">{row.mayDo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel oneview>
          <PanelHead title="Preferences & consent">
            <OneViewMark source="consent" />
          </PanelHead>
          <div className="px-4 py-3.5">
            <Kv
              wide
              items={[
                { label: "Outage notifications", mark: <OneViewMark source="consent" compact />, value: customer.consent.outageNotifications },
                { label: "Marketing contact", mark: <OneViewMark source="consent" compact />, value: customer.consent.marketingContact },
                {
                  label: "Use service data for offers",
                  mark: <OneViewMark source="billComm" compact />,
                  value: (
                    <span>
                      {customer.consent.serviceDataForOffers}{" "}
                      <Button variant="link" size="sm" className="h-auto px-1" onClick={onConsent}>History</Button>
                    </span>
                  ),
                },
              ]}
            />
            <p className="mt-2 text-[11.5px] text-muted-foreground">Consent changes take effect immediately and are journaled with date and channel.</p>
          </div>
        </Panel>
      </div>
      <Panel oneview>
        <PanelHead title="Recent activity">
          <OneViewMark source="activity" />
        </PanelHead>
        <ul>
          {customer.timeline.map((item) => (
            <li key={item.title} className="grid grid-cols-[150px_1fr] gap-3.5 border-t border-[#e6ebf0] px-4 py-2.5 first:border-t-0">
              <span className="text-xs text-muted-foreground">{item.when}</span>
              <div>
                <strong className="font-semibold">{item.title}</strong>
                <div className="mt-0.5 text-[12.5px] text-[#43535f]">{item.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

export function BillingTab({
  customer,
  perms,
  onInvoice,
  onCredit,
  onPayLink,
  onIvr,
}: {
  customer: Customer360;
  perms: Permissions;
  onInvoice: () => void;
  onCredit: () => void;
  onPayLink: () => void;
  onIvr: () => void;
}) {
  const max = Math.max(...customer.billing.trend.map((t) => t.amount));
  return (
    <div className="space-y-3.5">
      <div className="grid gap-3.5 md:grid-cols-2">
        <Panel oneview>
          <PanelHead title={customer.billing.invoiceTitle}>
            <OneViewMark source="invoice" />
          </PanelHead>
          <div className="px-4 py-3.5">
            <Kv
              items={[
                ...customer.billing.lines.map((line) => ({
                  label: line.label,
                  value: (
                    <span className="num">
                      {line.amount} {line.flag && <Pill tone="warn">{line.flag}</Pill>}
                    </span>
                  ),
                })),
                { label: customer.billing.totalLabel, value: <strong className="num">{customer.billing.totalAmount}</strong> },
              ]}
            />
            <div className="mt-3 flex gap-2">
              <span className="inline-flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={onInvoice}>View invoice PDF</Button>
                <OneViewMark source="invoicePdf" compact />
              </span>
              <span className="inline-flex items-center gap-1">
                <Button size="sm" variant="outline" disabled={!perms.adjust} onClick={onCredit}>Apply credit</Button>
                <OneViewMark source="credit" compact />
              </span>
            </div>
          </div>
        </Panel>
        <Panel oneview>
          <PanelHead title="Payment method">
            <OneViewMark source="payMethod" />
            <span className="flex-1" />
            <Pill tone="info">PCI protected</Pill>
          </PanelHead>
          <div className="px-4 py-3.5">
            <Kv
              items={[
                { label: "AutoPay", mark: <OneViewMark source="autoPay" compact />, value: customer.billing.paymentMethod.autoPay },
                { label: "Wallet", mark: <OneViewMark source="payMethod" compact />, value: customer.billing.paymentMethod.wallet },
              ]}
            />
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={onPayLink}>Send secure payment link</Button>
              <Button size="sm" variant="outline" onClick={onIvr}>Transfer to phone payment (IVR)</Button>
            </div>
            <p className="mt-2 text-xs text-warn">
              Never ask for or key in full card numbers. Payments are taken only through the secure link or automated phone system.
            </p>
          </div>
        </Panel>
      </div>
      <Panel oneview>
        <PanelHead title="Billing trend — last 6 months">
          <OneViewMark source="billingTrend" />
        </PanelHead>
        <div className="px-4 py-3.5">
          <div className="flex h-[110px] items-end gap-2" role="img" aria-label="Monthly bill amounts April through September">
            {customer.billing.trend.map((bar) => (
              <div key={bar.label} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                <i
                  className={`block w-full max-w-[34px] rounded-t-[3px] border ${bar.hot ? "border-[#e3b8b6] bg-[#f4dede]" : "border-[#c4d7f0] bg-accent"}`}
                  style={{ height: `${Math.round((bar.amount / max) * 100)}%` }}
                />
                <span className="text-[10.5px] text-muted-foreground">{bar.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11.5px] text-muted-foreground">{customer.billing.trendNote}</p>
        </div>
      </Panel>
      <Panel oneview>
        <PanelHead title="Usage detail">
          <OneViewMark source="usage" />
          <span className="flex-1" />
          {customer.usageStatus === "locked" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warn-wash px-2 py-0.5 text-[11.5px] font-semibold text-warn">
              Verify to view
            </span>
          )}
        </PanelHead>
        {customer.usageStatus === "locked" || !customer.usage ? (
          <div className="m-4 rounded-md border border-dashed border-border bg-secondary px-4 py-3 text-[12.5px] text-[#43535f]">
            {customer.usageStatus === "available"
              ? "No usage records returned for this account."
              : "Usage and call detail records are protected customer information (CPNI). Verify the customer’s identity to view."}
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
                <th className="px-3 py-2">Line</th>
                <th className="px-3 py-2">Data used</th>
                <th className="px-3 py-2">Voice</th>
                <th className="px-3 py-2">Roaming</th>
              </tr>
            </thead>
            <tbody>
              {customer.usage.map((row) => (
                <tr key={row.line} className="border-t border-[#e6ebf0]">
                  <td className="px-3 py-2 num">{row.line}</td>
                  <td className="px-3 py-2">{row.dataUsed}</td>
                  <td className="px-3 py-2 num">{row.voice}</td>
                  <td className="px-3 py-2 num">{row.roaming}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}

export function ServicesTab({
  customer,
  perms,
  onAction,
}: {
  customer: Customer360;
  perms: Permissions;
  onAction: (action: string, summary: string) => void;
}) {
  const lock = (key: "orders" | "services" | "offers") =>
    perms[key] ? (key === "offers" ? "" : "") : `Needs ${minRoleFor(key)}`;
  return (
    <div className="space-y-3.5">
      <Panel oneview>
        <PanelHead title="Mobile lines">
          <OneViewMark source="mobile" />
          <span className="flex-1" />
          <Button size="sm" variant="link" disabled={!perms.orders} onClick={() => onAction("order.started", "Add a line flow opened")}>
            Add a line { !perms.orders && <span className="ml-1 text-[11px] text-muted-foreground">{lock("orders")}</span> }
          </Button>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Line</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {customer.services.mobile.map((row) => (
              <tr key={row.line} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2 num">{row.line}</td>
                <td className="px-3 py-2">{row.plan}</td>
                <td className="px-3 py-2">{row.device}</td>
                <td className="px-3 py-2"><Pill tone={StatusTone(row.status)}>{row.status}</Pill></td>
                <td className="px-3 py-2">
                  <Button size="sm" variant="link" disabled={!perms.services} onClick={() => onAction("line.action", `Manage line ${row.line.slice(-4)} opened`)}>Manage</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel oneview>
        <PanelHead title="Home internet">
          <OneViewMark source="internet" />
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Service</th>
              <th className="px-3 py-2">Speed</th>
              <th className="px-3 py-2">Equipment</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2">{customer.services.internet.service}</td>
              <td className="px-3 py-2">{customer.services.internet.speed}</td>
              <td className="px-3 py-2">{customer.services.internet.equipment}</td>
              <td className="px-3 py-2"><Pill tone="warn">{customer.services.internet.status}</Pill></td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center gap-1">
                  <Button size="sm" variant="link" onClick={() => onAction("diagnostics.run", "Remote line test started for Fiber 500")}>
                    Run diagnostics
                  </Button>
                  <OneViewMark source="diagnostics" compact />
                </span>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="m-4 rounded-md border border-dashed border-[#dc2626] bg-[#fee2e2]/40 px-4 py-3 text-[12.5px] text-[#43535f]">
          <span className="mr-1.5 inline-flex align-middle"><OneViewMark source="outage" compact /></span>
          {customer.services.internet.notice}
        </div>
      </Panel>
      <Panel oneview>
        <PanelHead title="Home phone">
          <OneViewMark source="phone" />
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Service</th>
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Equipment</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-3 py-2">{customer.services.phone.service}</td>
              <td className="px-3 py-2 num">{customer.services.phone.number}</td>
              <td className="px-3 py-2">{customer.services.phone.equipment}</td>
              <td className="px-3 py-2"><Pill tone="ok">{customer.services.phone.status}</Pill></td>
              <td className="px-3 py-2">
                <Button size="sm" variant="link" disabled={!perms.services} onClick={() => onAction("line.action", "Voicemail and calling features viewed")}>Manage features</Button>
              </td>
            </tr>
          </tbody>
        </table>
        <p className="px-4 py-2 text-[11.5px] text-muted-foreground">{customer.services.phone.note}</p>
      </Panel>
      <Panel oneview>
        <PanelHead title="TV & streaming">
          <OneViewMark source="tv" />
          <span className="flex-1" />
          <span className="text-[11.5px] text-muted-foreground">Plan: {customer.services.tv.plan}</span>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Where</th>
              <th className="px-3 py-2">Device</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {customer.services.tv.devices.map((row) => (
              <tr key={row.where} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2">{row.where}</td>
                <td className="px-3 py-2">{row.device}</td>
                <td className="px-3 py-2"><Pill tone={StatusTone(row.status)}>{row.status}</Pill></td>
                <td className="px-3 py-2">
                  <Button size="sm" variant="link" onClick={() => onAction("device.action", `${row.where} device action`)}>
                    {row.where === "Living room" ? "Restart" : row.where === "Bedroom" ? "Refresh access" : "View devices"}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-2 text-[11.5px] text-muted-foreground">{customer.services.tv.note}</p>
      </Panel>
      <Panel oneview>
        <PanelHead title="Add-ons & extras">
          <OneViewMark source="addons" />
          <span className="flex-1" />
          <Button size="sm" variant="link" disabled={!perms.offers} onClick={() => onAction("offer.presented", "Available add-ons listed")}>
            Browse add-ons
          </Button>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Add-on</th>
              <th className="px-3 py-2">Applies to</th>
              <th className="px-3 py-2">Price</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {customer.services.addons.map((row) => (
              <tr key={row.name} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2">{row.name}</td>
                <td className="px-3 py-2">{row.appliesTo}</td>
                <td className="px-3 py-2 num">{row.price}</td>
                <td className="px-3 py-2"><Pill tone={StatusTone(row.status)}>{row.status}</Pill></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

export function InteractionsTab({ customer }: { customer: Customer360 }) {
  return (
    <Panel oneview>
      <PanelHead title="Interaction history">
        <OneViewMark source="activity" />
        <span className="flex-1" />
        <span className="text-[11.5px] text-muted-foreground">All channels · journaled automatically</span>
      </PanelHead>
      <ul>
        {customer.interactions.map((item) => (
          <li key={item.title} className="grid grid-cols-[150px_1fr] gap-3.5 border-t border-[#e6ebf0] px-4 py-2.5 first:border-t-0">
            <span className="text-xs text-muted-foreground">{item.when}</span>
            <div>
              <strong className="font-semibold">{item.title}</strong>
              <div className="mt-0.5 text-[12.5px] text-[#43535f]">{item.detail}</div>
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function TicketsTab({
  customer,
  onCreate,
  onOpen,
}: {
  customer: Customer360;
  onCreate: () => void;
  onOpen: (id: string) => void;
}) {
  return (
    <Panel>
      <PanelHead title="BMC Helix tickets">
        <span className="flex-1" />
        <Button size="sm" onClick={onCreate}>Create ticket</Button>
      </PanelHead>
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
            <th className="px-3 py-2">Ticket</th>
            <th className="px-3 py-2">Summary</th>
            <th className="px-3 py-2">Priority</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2">Assigned</th>
          </tr>
        </thead>
        <tbody>
          {customer.tickets.map((ticket) => (
            <tr key={ticket.id} className="cursor-pointer border-t border-[#e6ebf0] hover:bg-secondary" onClick={() => onOpen(ticket.id)}>
              <td className="px-3 py-2 num">{ticket.id}</td>
              <td className="px-3 py-2">{ticket.summary}</td>
              <td className="px-3 py-2"><Pill tone={ticket.priority === "P2" ? "warn" : "neutral"}>{ticket.priority}</Pill></td>
              <td className="px-3 py-2"><Pill tone={StatusTone(ticket.status)}>{ticket.status}</Pill></td>
              <td className="px-3 py-2">{ticket.assigned}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="px-4 py-2.5 text-[11.5px] text-muted-foreground">Synced with BMC Helix ITSM. A ticket is created or linked for every customer interaction at wrap-up.</p>
    </Panel>
  );
}

export function InsightRail({
  customer,
  perms,
  onAction,
  onCredit,
}: {
  customer: Customer360;
  perms: Permissions;
  onAction: (action: string, summary: string) => void;
  onCredit: () => void;
}) {
  return (
    <aside className="space-y-3.5">
      <Panel className="border-[#d8d3ee]">
        <div className="flex items-center gap-2.5 rounded-t-[10px] border-b border-[#d8d3ee] bg-intent-wash px-4 py-3">
          <h3 className="text-[13.5px] font-semibold text-intent">Likely contact reason</h3>
          <span className="flex-1" />
          <Chip>Updated 10:02 AM</Chip>
        </div>
        {customer.predictions.items.map((item) => (
          <div key={item.reason} className="border-b border-[#e6ebf0] px-4 py-2.5 last:border-b-0">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[13px] font-semibold">{item.label}</span>
              <span className="text-xs font-bold text-intent num">{Math.round(item.confidence * 100)}%</span>
            </div>
            <div className="my-1.5 h-1 rounded-sm bg-[#e6ebf0]">
              <i className="block h-full rounded-sm bg-intent" style={{ width: `${Math.round(item.confidence * 100)}%` }} />
            </div>
            <div className="text-[11.5px] text-[#43535f]">{item.signals.map((s) => s.text).join(" · ")}</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {item.signals.map((s) => (
                <span key={s.text} className="rounded-full border border-[#e6ebf0] bg-secondary px-1.5 text-[10.5px] text-muted-foreground capitalize">{s.source.replace(/([A-Z])/g, " $1")}</span>
              ))}
            </div>
          </div>
        ))}
        <div className="border-t border-[#e6ebf0] px-4 py-2.5 text-[11px] text-muted-foreground" title={`Model ${customer.predictions.modelVersion}`}>
          Predicted from account, billing, interaction, engagement, app usage, and cohort signals. Predictions are guidance — confirm with the customer. Model {customer.predictions.modelVersion}.
        </div>
      </Panel>
      <Panel>
        <PanelHead title="Suggested actions" />
        <div className="flex flex-col">
          <button type="button" className="border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary" onClick={() => onAction("guidance.opened", "Explain roaming charges walkthrough")}>
            Explain the roaming charges on this bill
          </button>
          <button type="button" disabled={!perms.adjust} className="flex items-center border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-45" onClick={onCredit}>
            Apply outage or goodwill credit
            <span className="ml-auto text-[11px] text-muted-foreground">{perms.adjust ? `up to $${perms.adjustLimit}` : `Needs ${minRoleFor("adjust")}`}</span>
          </button>
          <button type="button" disabled={!perms.offers} className="flex items-center border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-45" onClick={() => onAction("offer.presented", "International travel pass $10/mo")}>
            Offer international travel pass
            <span className="ml-auto text-[11px] text-muted-foreground">{perms.offers ? "" : `Needs ${minRoleFor("offers")}`}</span>
          </button>
          <button type="button" className="border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary" onClick={() => onAction("ticket.linked", "Customer added to INC0049307 outage notifications")}>
            Add to outage updates (INC0049307)
          </button>
          <button type="button" disabled={!perms.retention} className="flex items-center px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-45" onClick={() => onAction("retention.started", "Retention review started")}>
            Start retention review
            <span className="ml-auto text-[11px] text-muted-foreground">{perms.retention ? "" : `Needs ${minRoleFor("retention")}`}</span>
          </button>
        </div>
      </Panel>
    </aside>
  );
}
